import variable from "../common/variable";

// Start
/**
 * Rating 组件类 - 圆形数字范围评分组件
 * 用于1-5分的标准评分范围选择
 * @class TTRating
 */
class TTRange {
	/**
	 * 创建 Rating 组件实例
	 * @param {string} containerId - 容器ID
	 * @param {string} resultId - 结果显示元素ID
	 * @param {Object} config - 选择变化回调函数
	 */
	constructor(config) {
		this.init(config);
	}

	/**
	 * 初始化组件
	 * @private
	 */
	init(config) {
		const { title, type, isMiddle, id, labels, targetNode, onChange } = config;
		this.id = id;
		this.type = type;
		this.labels = labels;
		this.title = title;
		this.onChange = onChange;
		this.isMiddle = isMiddle;
		let subNum = 0;
		if (isMiddle) {
			subNum = 3;
		}
		targetNode.insertAdjacentHTML(
			"beforeend",
			`<div class="tt-container">
        <div class="tt-attr-title">${title}</div>
        <div class="tt-range${
					isMiddle ? " tt-middle-range" : ""
				} tt-attr-container" data-id="${id}">
            ${Array.from({ length: 5 }, (_, i) => i + 1).reduce(
							(acc, num, index) =>
								acc +
								`<div class="tt-range-option" data-value="${index + 1 - subNum}">${num}</div>`,
							""
						)}
        ${
					isMiddle
						? `<svg class="tt-range-check" width="20" height="15" viewBox="0 0 20 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M18.9488 1.55167C19.4175 2.0203 19.4175 2.78009 18.9488 3.24872L8.54882 13.6487C8.08019 14.1174 7.32039 14.1174 6.85176 13.6487L1.65176 8.44872C1.18314 7.98009 1.18314 7.2203 1.65176 6.75167C2.12039 6.28304 2.88019 6.28304 3.34882 6.75167L7.70029 11.1031L17.2518 1.55167C17.7204 1.08304 18.4802 1.08304 18.9488 1.55167Z" fill="white" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>`
						: ""
				}
        </div>
        <div class="tt-range-labels" ${isMiddle ? "data-middle" : ""}>
          ${labels.map((label) => `<span>${label}</span>`).join("")}
      </div>
    </div>`
		);
		this.container = document.querySelector(`.tt-range[data-id="${id}"]:not([data-handled])`);
		this.container.dataset.handled = "true";
		this.bindEvents();
	}

	/**
	 * 绑定事件
	 * @private
	 */
	bindEvents() {
		const options = this.container.querySelectorAll(".tt-range-option");
		options.forEach((option) => {
			option.addEventListener("click", (e) => {
				this.selectOption(e.target);
			});
		});
	}

	/**
	 * 选择选项
	 * @private
	 * @param {HTMLElement} selectedOption - 被选中的选项元素
	 */
	selectOption(selectedOption) {
		if (selectedOption.classList.contains("tt-selected")) {
			this.reset();
			return;
		}

		// 清除之前的选中状态
		this.container.querySelectorAll(".tt-range-option").forEach((option) => {
			option.classList.remove("tt-selected");
		});

		// 设置新的选中状态
		selectedOption.classList.add("tt-selected");
		this.selectedValue = selectedOption.dataset.value;

		// 移动SVG到选中位置
		if (this.type === "centered_range") {
			this.moveSvgToSelected(selectedOption);
		}

		// 触发回调
		if (this.onChange) {
			this.onChange(this.selectedValue);
		}
	}
	/**
	 * 移动SVG到选中的选项位置
	 * @private
	 * @param {HTMLElement} selectedOption - 被选中的选项元素
	 */
	moveSvgToSelected(selectedOption) {
		const svg = this.container.querySelector(".tt-range-check");
		const containerRect = this.container.getBoundingClientRect();
		const optionRect = selectedOption.getBoundingClientRect();

		// 计算相对于容器的位置
		const leftPosition = optionRect.left - containerRect.left + optionRect.width / 2 - 10; // 10 = svg width / 2

		// 设置SVG位置并显示
		svg.style.left = leftPosition + "px";
		svg.classList.add("tt-visible");
	}

	/**
	 * 获取当前选中值
	 * @returns {string|null} 当前选中的值
	 */
	getValue() {
		if (this.selectedValue) {
			return [this.selectedValue];
		}
		return [];
	}

	/**
	 * 设置选中值
	 * @param {string} value - 要设置的值
	 */
	setValue(value) {
		const option = this.container.querySelector(`[data-value="${value}"]`);
		if (option) {
			this.selectOption(option);
		}
	}

	/**
	 * 重置选择
	 */
	reset() {
		this.container.querySelectorAll(".tt-range-option").forEach((option) => {
			option.classList.remove("tt-selected");
		});
		// 隐藏SVG
		if (this.isMiddle) {
			const svg = this.container.querySelector(".tt-range-check");
			svg.classList.remove("tt-visible");
		}
		this.selectedValue = null;
	}
}

/**
 * Choice 组件类 - 标签选择器（支持单选/多选）
 * @class TTChoice
 */
class TTChoice {
	/**
	 * 创建 Choice 组件实例
	 * @param {string} containerId - 容器ID
	 * @param {string} resultId - 结果显示元素ID
	 * @param {boolean} [multiple=false] - 是否支持多选
	 * @param {Function} [onChange] - 选择变化回调函数
	 */
	constructor(config) {
		this.selectedValues = [];
		this.init(config);
	}

	/**
	 * 初始化组件
	 * @private
	 */
	init(config) {
		const { title, id, labels, type, targetNode, options, multiple, onChange } = config;
		const allinone = 1;
		const separateWriteReview = 2;
		this.multiple = multiple;
		this.id = id;
		this.type = type;
		this.title = title;
		this.labels = labels;
		this.onChange = onChange;
		const separateWords = variable.lang.separate_review_form;
		const allinoneWords = variable.lang.all_in_one_from;
		let separateTip = "",
			allinoneTip = "";
		const wordKey = multiple ? "select_all" : "choose_one";
		if (variable.writeForm === allinone) {
			allinoneTip = ` (${allinoneWords[wordKey]})`;
		} else if (variable.writeForm === separateWriteReview) {
			separateTip = `<div class="tt-attr-subtitle">${separateWords[wordKey]}</div>`;
		}
		let optionHtml = `<div class="tt-choice tt-attr-container" data-id="${id}">
			${options.reduce((pre, option) => {
				return pre + `<div class="tt-choice-option" data-value="${option}">${option}</div>`;
			}, "")}
			</div>`;
		if (variable.writeForm === separateWriteReview && type.includes("choice")) {
			optionHtml = `<div style="height:188px;display:flex;align-items:center;" data-type="${type}">${optionHtml}</div>`;
		}
		targetNode.insertAdjacentHTML(
			"beforeend",
			`<div class="tt-container">
			<div class="tt-attr-title">${title}${allinoneTip}</div>
			${separateTip}
			${optionHtml}
    </div>`
		);
		this.container = document.querySelector(`.tt-choice[data-id="${id}"]:not([data-handled])`);
		this.container.dataset.handled = "true";

		this.bindEvents();
	}

	/**
	 * 绑定事件
	 * @private
	 */
	bindEvents() {
		const options = this.container.querySelectorAll(".tt-choice-option");
		options.forEach((option) => {
			option.addEventListener("click", (e) => {
				this.toggleOption(e.target);
			});
		});
	}

	/**
	 * 切换选项状态
	 * @private
	 * @param {HTMLElement} option - 被点击的选项元素
	 */
	toggleOption(option) {
		const value = option.dataset.value;
		const isSelected = option.classList.contains("tt-selected");

		if (this.multiple) {
			// 多选模式
			if (isSelected) {
				// 取消选中
				option.classList.remove("tt-selected");
				this.selectedValues = this.selectedValues.filter((v) => v !== value);
			} else {
				// 选中
				option.classList.add("tt-selected");
				this.selectedValues.push(value);
			}
		} else {
			// 单选模式
			if (isSelected) {
				// 如果已选中，则取消选中
				option.classList.remove("tt-selected");
				this.selectedValues = [];
			} else {
				// 清除其他选中状态
				this.container.querySelectorAll(".tt-choice-option").forEach((opt) => {
					opt.classList.remove("tt-selected");
				});
				// 选中当前项
				option.classList.add("tt-selected");
				this.selectedValues = [value];
			}
		}

		// 触发回调
		if (this.onChange) {
			this.onChange(this.selectedValues, this.multiple);
		}
	}

	/**
	 * 设置选择模式
	 * @param {boolean} multiple - 是否为多选模式
	 */
	setMultiple(multiple) {
		this.multiple = multiple;
		if (!multiple && this.selectedValues.length > 1) {
			// 从多选切换到单选时，只保留第一个选中项
			const firstValue = this.selectedValues[0];
			this.reset();
			this.setValue([firstValue]);
		}
	}

	/**
	 * 获取当前选中值
	 * @returns {Array<string>} 当前选中的值数组
	 */
	getValue() {
		return [...this.selectedValues];
	}

	/**
	 * 设置选中值
	 * @param {Array<string>} values - 要设置的值数组
	 */
	setValue(values) {
		this.reset();
		values.forEach((value) => {
			const option = this.container.querySelector(`[data-value="${value}"]`);
			if (option) {
				option.classList.add("tt-selected");
				if (!this.selectedValues.includes(value)) {
					this.selectedValues.push(value);
				}
			}
		});
	}

	/**
	 * 重置选择
	 */
	reset() {
		this.container.querySelectorAll(".tt-choice-option").forEach((option) => {
			option.classList.remove("tt-selected");
		});
		this.selectedValues = [];
	}
}

function getAttributeSelected() {
	const attributes = [];
	const attributeInstances = variable.attributeInstances;
	attributeInstances.forEach((instance) => {
		const { id, type, title } = instance;
		const info = {
			id,
			type,
			title
		};
		const value = instance.getValue();

		let options = instance.labels;
		if (type.includes("choice")) {
			options = value;
		} else if (type.includes("range")) {
			info.range_value = value[0];
		}
		if (value.length > 0) {
			attributes.push({
				...info,
				options
			});
		}
	});
	return attributes;
}

function insertAttrOption(targetNode, config, onChange) {
	const { title, type, id, options } = config;
	if (type.includes("choice")) {
		const choiceConfig = {
			title,
			multiple: type === "multiple_choice",
			id,
			targetNode,
			options,
			type,
			onChange
		};
		const choiceComponent = new TTChoice(choiceConfig);
		variable.attributeInstances.push(choiceComponent);
	} else if (type.includes("range")) {
		const middleRangeConfig = {
			labels: options,
			isMiddle: type === "centered_range",
			title,
			id,
			targetNode,
			type,
			onChange
		};
		const rangeComponent = new TTRange(middleRangeConfig);
		variable.attributeInstances.push(rangeComponent);
	}
}

function resetAttributeSelected() {
	variable.attributeInstances.forEach((instance) => {
		instance.reset();
	});
}

/**
 * 生成范围条HTML
 * @param {Object} options - 配置选项
 * @param {string} options.type - 类型: 'range' | 'centered'
 * @param {number} options.min - 最小值
 * @param {number} options.max - 最大值
 * @param {number} options.value - 当前值
 * @param {number} [options.segments=4] - 分段数量
 * @param {string} [options.title] - 标题
 * @param {string[]} [options.labels] - 标签数组
 * @param {string} [options.size='normal'] - 尺寸: 'small' | 'normal' | 'large'
 * @returns {string} HTML字符串
 */
function createRangeDisplayHTML(options = {}) {
	const {
		type = "range",
		value = 3,
		segments = 4,
		title = "",
		labels = [],
		size = "normal",
		style = "rounded"
	} = options;

	let { min, max } = options;
	if (!min) {
		min = 1;
		if (type.includes("centered")) {
			min = -2;
		}
	}
	if (!max) {
		max = 5;
		if (type.includes("centered")) {
			max = 2;
		}
	}

	// 生成默认标签
	function generateDefaultLabels() {
		if (type === "centered") {
			return [min.toString(), "Perfect", max.toString()];
		} else {
			return [min.toString(), max.toString()];
		}
	}

	// 生成分段HTML - 使用渐变背景的简化版本
	function generateSegmentsHTML() {
		// 分段只用于显示分割线，不处理填充
		return Array.from({ length: segments }, (_, i) => {
			return `<div class="tt-range-display__segment" data-segment="${i + 1}"></div>`;
		}).join("");
	}

	// 生成轨道渐变样式
	function generateTrackGradient() {
		const percentage = ((value - min) / (max - min)) * 100;

		if (type === "range") {
			if (style === "rounded") {
				// 圆角高低范围：从左侧渐变到滑块位置
				return `background: linear-gradient(to right, var(--attr-color) ${percentage}%, var(--attr-bg-color) ${percentage}%)`;
			} else {
				// 方角高低范围：无渐变，使用纯背景色
				return `background-color: var(--attr-bg-color)`;
			}
		} else if (type === "centered") {
			const centerPosition = 50; // 50%

			if (style === "rounded") {
				// 圆角居中范围：从中心到滑块位置的渐变
				if (percentage > centerPosition) {
					// 右侧填充：从中心到滑块
					return `background: linear-gradient(to right, var(--attr-bg-color) 0%, var(--attr-bg-color) ${centerPosition}%, var(--attr-color) ${centerPosition}%, var(--attr-color) ${percentage}%, var(--attr-bg-color) ${percentage}%, var(--attr-bg-color) 100%)`;
				} else if (percentage < centerPosition) {
					// 左侧填充：从滑块到中心
					return `background: linear-gradient(to right, var(--attr-bg-color) 0%, var(--attr-bg-color) ${percentage}%, var(--attr-color) ${percentage}%, var(--attr-color) ${centerPosition}%, var(--attr-bg-color) ${centerPosition}%, var(--attr-bg-color) 100%)`;
				} else {
					// 正好在中心，无填充
					return `background-color: var(--attr-bg-color)`;
				}
			} else {
				// 方角居中范围：无渐变，使用纯背景色
				return `background-color: var(--attr-bg-color)`;
			}
		}

		return `background-color: var(--attr-bg-color)`;
	}

	// 生成标签HTML
	function generateLabelsHTML() {
		const finalLabels = labels.length > 0 ? labels : generateDefaultLabels();
		const labelsClass = finalLabels.length === 3 ? "tt-range-display__labels--three" : "";
		const labelsHtml = finalLabels
			.map((label) => `<div class="tt-range-display__label">${label}</div>`)
			.join("");

		return `<div class="tt-range-display__labels ${labelsClass}">${labelsHtml}</div>`;
	}

	// 计算指示器位置 - 圆角类型边缘调整
	const leftVal = (value - min) / (max - min);
	let indicatorPosition = leftVal * 100 + "%";
	if (style === "rounded") {
		if (leftVal === 0) {
			indicatorPosition = `calc(${indicatorPosition} + 1px)`;
		} else if (leftVal === 1) {
			indicatorPosition = `calc(${indicatorPosition} - 1px)`;
		}
	}

	// 生成完整HTML
	return `
					<div class="tt-range-display tt-range-display--${type} tt-range-display--${size} tt-range-display--${style}">
							${title ? `<div class="tt-range-display__title">${title}</div>` : ""}
							<div class="tt-range-display__container">
									<div class="tt-range-display__track" style="${generateTrackGradient()}">
											<div class="tt-range-display__segments">
													${generateSegmentsHTML()}
											</div>
											${type === "centered" ? '<div class="tt-range-display__center-point"></div>' : ""}
											${
												type === "centered" && style === "rounded"
													? '<div class="tt-range-display__center-line"></div>'
													: ""
											}
									</div>
									<div class="tt-range-display__indicator" style="left: ${indicatorPosition}"></div>
							</div>
							${generateLabelsHTML()}
					</div>
			`;
}
// End

export {
	TTRange,
	TTChoice,
	getAttributeSelected,
	insertAttrOption,
	resetAttributeSelected,
	createRangeDisplayHTML
};
